import type { AppStore } from '../core/store';
import { MapExportService } from '../export/MapExportService';
import { CanvasExportSecurityError, type MapExportBackground, type MapExportContent, type MapExportOptions, type MapExportResolution, type MapExportScope } from '../export/mapExport';
import { buildLegendComposition } from '../presentation/legend';

const required = <T extends Element = HTMLElement>(selector: string): T => {
  const element = document.querySelector<T>(selector); if (!element) throw new Error(`No se encontró ${selector}`); return element;
};

export class MapExportController {
  constructor(private readonly store: AppStore, private readonly service: MapExportService) {
    required<HTMLSelectElement>('#export-content').addEventListener('change', () => this.syncContent());
    required<HTMLSelectElement>('#export-background').addEventListener('change', () => this.syncBackground());
    required<HTMLInputElement>('#export-basemap').addEventListener('change', () => {
      required<HTMLSelectElement>('#export-background').value = required<HTMLInputElement>('#export-basemap').checked ? 'basemap' : 'white';
    });
    required<HTMLSelectElement>('#export-margin').addEventListener('change', () => {
      required<HTMLElement>('#export-custom-margin-row').hidden = required<HTMLSelectElement>('#export-margin').value !== 'custom';
    });
    required('#export-map').addEventListener('click', () => void this.run());
    required('#export-without-basemap').addEventListener('click', () => {
      required<HTMLInputElement>('#export-basemap').checked = false; required<HTMLSelectElement>('#export-background').value = 'white'; void this.run();
    });
    this.syncContent(); this.syncBackground();
  }

  render(): void {
    const state = this.store.getState(); const legendOnly = required<HTMLSelectElement>('#export-content').value === 'legend';
    required<HTMLButtonElement>('#export-map').disabled = legendOnly ? !buildLegendComposition(state) : !state.layers.some(({ visible, opacity }) => visible && opacity > 0);
  }

  private options(): MapExportOptions {
    const marginValue = required<HTMLSelectElement>('#export-margin').value;
    const marginPercent = marginValue === 'custom' ? Number(required<HTMLInputElement>('#export-custom-margin').value) : Number(marginValue);
    return {
      content: required<HTMLSelectElement>('#export-content').value as MapExportContent,
      scope: required<HTMLSelectElement>('#export-scope').value as MapExportScope,
      marginPercent: Number.isFinite(marginPercent) ? marginPercent : 5,
      resolution: required<HTMLSelectElement>('#export-resolution').value as MapExportResolution,
      background: required<HTMLSelectElement>('#export-background').value as MapExportBackground,
      showLabels: required<HTMLInputElement>('#export-labels').checked,
      showLegend: required<HTMLInputElement>('#export-legend').checked,
      showScale: required<HTMLInputElement>('#export-scale').checked,
      showNorth: required<HTMLInputElement>('#export-north').checked,
      showBasemap: required<HTMLInputElement>('#export-basemap').checked,
    };
  }

  private async run(): Promise<void> {
    const status = required('#export-status'); const fallback = required<HTMLButtonElement>('#export-without-basemap'); fallback.hidden = true;
    const options = this.options(); status.textContent = 'Preparando composición cartográfica…'; required<HTMLButtonElement>('#export-map').disabled = true;
    try {
      const result = await this.service.export(this.store.getState(), options);
      status.textContent = `${result.filename} · ${result.width.toLocaleString('es-NI')} × ${result.height.toLocaleString('es-NI')} px`;
    } catch (error) {
      status.textContent = error instanceof Error ? error.message : 'No fue posible exportar el mapa.';
      fallback.hidden = !(error instanceof CanvasExportSecurityError);
    } finally { this.render(); }
  }

  private syncContent(): void {
    const content = required<HTMLSelectElement>('#export-content').value as MapExportContent;
    required<HTMLElement>('#export-map-options').hidden = content === 'legend';
    const legend = required<HTMLInputElement>('#export-legend'); legend.disabled = content !== 'map-legend'; legend.checked = content === 'map-legend';
    this.render();
  }

  private syncBackground(): void {
    required<HTMLInputElement>('#export-basemap').checked = required<HTMLSelectElement>('#export-background').value === 'basemap';
  }
}
